import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { StepHelpers } from "@shared/components/Stepper";
import { Button } from "@shared/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit3,
  Trash2,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Info,
  Plus,
} from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { CreateSyncFlowDialogue } from "./createSyncFlowDialogue";
import { PageHeader } from "@shared/components/PageHeader";
import { useGetChoices } from "@features/ix/hooks";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetAvailableFlows,
  useGetConnectionFlows,
  useUpdateConnectionFlow,
  useDeleteConnectionFlow,
} from "@features/ix/erpIntegration/hooks";

interface SyncFlowsStepProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

interface SyncFlowItem {
  endpointId: string;
  isActive: boolean;
  objectType: string;
  operationKey: string;
  directionLabel: string;
  syncType: string;
  scheduleLabel?: string;
  lastRun: string;
  successRate: number;
  mappingPct: number;
}

const SyncFlowCard: React.FC<{
  item: SyncFlowItem;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (endpointId: string, updatedData: Partial<SyncFlowItem>) => void;
  onDelete: (endpointId: string) => void;
}> = ({ item, isExpanded, onToggleExpand, onUpdate, onDelete }) => {
  const { id } = useParams<{ id: string }>();
  const { data: directionData } = useGetChoices("direction");
  const { data: syncTypeData } = useGetChoices("schedule_type");
  const { data: availableFlowsData } = useGetAvailableFlows(id);

  const objectTypeChoices = useMemo(
    () =>
      availableFlowsData?.map((d: any) => ({
        value: d.objectType,
        label: d.label,
      })) || [],
    [availableFlowsData],
  );

  const directionChoices = useMemo(
    () =>
      directionData?.map((d: any) => ({
        value: d.key,
        label: d.value,
      })) || [],
    [directionData],
  );

  const syncTypeChoices = useMemo(
    () =>
      syncTypeData?.map((d: any) => ({
        value: d.key,
        label: d.value,
      })) || [],
    [syncTypeData],
  );

  // 1. Define fields configuration for this card's form using standard select types
  const fields: FormField[] = useMemo(
    () => [
      {
        name: "directionLabel",
        label: "Direction",
        type: "select",
        required: false,
        disabled: false,
        placeholder: "Select direction",
        className: "col-span-2",
        options: directionChoices,
        defaultValue: item.directionLabel,
      },
      {
        name: "syncType",
        label: "Sync type",
        type: "select",
        required: false,
        disabled: (values: any) => Number(values.directionLabel) === 1,
        helperText: "Not configurable for Inbound flows.",
        placeholder: "Select sync type",
        className: "col-span-2",
        defaultValue: item.syncType,
        options: [
          { value: "realtime", label: "Real-Time" },
          { value: "scheduled", label: "Scheduled" },
          { value: "manual", label: "Manual" },
        ],
      },
      {
        name: "scheduleLabel",
        label: "Schedule",
        type: "select",
        required: false,
        disabled: false,
        placeholder: "Select schedule",
        showWhen: (values: any) => values.syncType === "scheduled" && Number(values.directionLabel) !== 1,
        className: "col-span-2",
        options: syncTypeChoices,
        defaultValue: item.scheduleLabel || "",
      },
    ],
    [
      directionChoices,
      syncTypeChoices,
      item.directionLabel,
      item.syncType,
      item.scheduleLabel,
    ],
  );

  // 2. Initialize form state
  const form = FormService.createForm({
    fields,
    mode: "onChange",
    defaultValues: {
      objectType: item.objectType,
      directionLabel: item.directionLabel,
      syncType: item.syncType,
      scheduleLabel: item.scheduleLabel || "",
    },
  });

  // Keep form in sync when props change
  // useEffect(() => {
  //   form.reset({
  //     objectType: item.objectType,
  //     directionLabel: item.directionLabel,
  //     syncType: item.syncType,
  //     scheduleLabel: item.scheduleLabel || "",
  //   });
  // }, [
  //   item.objectType,
  //   item.directionLabel,
  //   item.syncType,
  //   item.scheduleLabel,
  //   form,
  // ]);

  // 3. Sync form changes with global flows list
  const handleFieldChange = useCallback(
    (name: string, val: any) => {
      let payload: any = { [name]: val };
      if (name === "directionLabel" && Number(val) === 1) {
        payload = { ...payload, syncType: null, scheduleLabel: null };
        form.setValue("syncType", "");
        form.setValue("scheduleLabel", "");
      }
      onUpdate(item.endpointId, payload);
    },
    [item.endpointId, onUpdate, form],
  );

  const handleToggleActive = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUpdate(item.endpointId, { isActive: !item.isActive });
  };

  const renderDirectionBadge = () => {
    const directionKey = Number(item.directionLabel);
    const isLtr = directionKey === 0;
    const isRtl = directionKey === 1;

    return (
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="px-2 py-0.5 bg-gray-100 text-gray-650 text-[11px] font-bold rounded border border-gray-200">
          SMART360
        </span>
        <span className="text-indigo-500 font-extrabold text-xs">
          {isLtr ? "→" : isRtl ? "←" : "↔"}
        </span>
        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[11px] font-bold rounded border border-indigo-150">
          Neptune
        </span>
      </div>
    );
  };

  const isSuccessHigh = item.successRate >= 98;

  return (
    <div
      className={`border rounded-2xl transition-all duration-200 overflow-hidden shadow-sm bg-white mb-4 ${
        item.isActive ? "border-gray-200" : "border-gray-250 opacity-90"
      }`}
    >
      {/* Header Row */}
      <div
        onClick={onToggleExpand}
        className="p-5 flex items-center justify-between cursor-pointer hover:bg-slate-50/40 select-none"
      >
        <div className="flex items-center gap-4">
          {/* Custom toggle switch */}
          <button
            type="button"
            onClick={handleToggleActive}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none ${
              item.isActive ? "bg-[#5850ec]" : "bg-gray-200"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                item.isActive ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>

          <div
            className={`flex flex-col gap-1.5 transition-opacity duration-150 ${item.isActive ? "opacity-100" : "opacity-65"}`}
          >
            <span className="text-base font-bold text-gray-900 leading-none">
              {objectTypeChoices.find((opt) => opt.value === item.objectType)
                ?.label || item.objectType}
            </span>
            <div className="flex items-center gap-3 text-xs text-gray-500 font-medium">
              {renderDirectionBadge()}
              
              {Number(item.directionLabel) !== 0 && (
                <>
                  <span>
                    {(() => {
                      if (item.syncType === "scheduled") {
                        const label =
                          syncTypeChoices.find(
                            (opt) =>
                              Number(opt.value) === Number(item.scheduleLabel),
                          )?.label || "Scheduled";
                        return `Scheduled · ${label}`;
                      }
                      if (item.syncType === "manual") {
                        return "Manual";
                      }
                      return "Real-Time";
                    })()}
                  </span>
                  <span className="text-gray-300">|</span>
                </>
              )}
              
              <span>Mapping {item.mappingPct}%</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Success rate percentage pill */}
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-0.5 rounded-full border ${
              isSuccessHigh
                ? "bg-emerald-50 text-emerald-755 border-emerald-200"
                : "bg-amber-50 text-amber-755 border-amber-200"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isSuccessHigh ? "bg-emerald-500" : "bg-amber-500"
              }`}
            />
            {item.successRate}%
          </span>

          {/* Delete Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item.endpointId);
            }}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors flex items-center justify-center"
          >
            <Trash2 className="w-4 h-4" />
          </button>

          {/* Toggle Expand Icon */}
          <button
            type="button"
            className="p-0.5 text-gray-400 hover:text-gray-600 rounded-full transition-colors"
          >
            <div className="w-8 h-8 rounded-full border border-gray-200 hover:border-gray-300 flex items-center justify-center hover:bg-gray-50 hover:text-gray-700 transition-all select-none">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-600" />
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Expanded form area */}
      {isExpanded && (
        <div className="px-5 pb-5 pt-3 border-t border-gray-100 bg-white">
          <DynamicForm
            fields={fields}
            form={form}
            onSubmit={() => {}}
            onFieldChange={handleFieldChange}
            config={{
              showSubmitButton: false,
              showResetButton: false,
              showProgress: false,
              classes: FIELD_CLASSES,
            }}
            gridColumns={6}
            responsiveColumns={{ sm: 1, md: 6 }}
          />

          {/* Bottom actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-4">
            <span className="text-xs text-gray-400 font-semibold">
              Last run: {item.lastRun}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const mapBackendFlowToFrontend = (item: any): SyncFlowItem => {
  console.log("item:", item);
  return {
    endpointId: String(item.endpointId),
    isActive:
      item.is_active !== undefined
        ? Boolean(item.is_active)
        : item.isActive !== undefined
          ? Boolean(item.isActive)
          : false,
    objectType: item.object_type || item.objectType || "",
    operationKey:
      item.operation_key ||
      item.operationKey ||
      item.operation_Key ||
      item.operationKey ||
      "",
    directionLabel: item.directionLabel,
    syncType: item.sync_type || item.syncType || "realtime",
    scheduleLabel:
      item.schedule_label !== undefined && item.schedule_label !== null
        ? String(item.schedule_label)
        : item.scheduleLabel !== undefined && item.scheduleLabel !== null
          ? String(item.scheduleLabel)
          : item.schedule !== undefined && item.schedule !== null
            ? String(item.schedule)
            : "",
    lastRun: item.last_run || item.lastRun || "Never run",
    successRate:
      item.success_rate !== undefined && item.success_rate !== null
        ? Number(item.success_rate)
        : item.successRate !== undefined
          ? Number(item.successRate)
          : 100,
    mappingPct:
      item.mapping_pct !== undefined && item.mapping_pct !== null
        ? Number(item.mapping_pct)
        : item.mappingPct !== undefined && item.mappingPct !== null
          ? Number(item.mappingPct)
          : item.mappingProgress !== undefined && item.mappingProgress !== null
            ? Number(item.mappingProgress)
            : 100,
  };
};

export const SyncFlowsStep: React.FC<SyncFlowsStepProps> = ({
  stepHelpers,
  onNext,
  onPrevious,
  clearValidationError,
}) => {
  const { action, id } = useParams<{ action: string; id: string }>();
  const navigate = useNavigate();
  const { data: flowsData } = useGetConnectionFlows(id || "");
  const updateFlowMutation = useUpdateConnectionFlow(id);
  const deleteFlowMutation = useDeleteConnectionFlow(id);

  const [expandedEndpointId, setExpandedEndpointId] = useState<string | null>(
    null,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const flows = useMemo(() => {
    if (flowsData?.results && Array.isArray(flowsData.results)) {
      return flowsData.results.map(mapBackendFlowToFrontend);
    }
    return [];
  }, [flowsData]);

  const prevFlowsRef = useRef<string>("");

  // Keep the stepper state perfectly in sync with the fetched flows
  useEffect(() => {
    if (stepHelpers) {
      const flowsStr = JSON.stringify(flows);
      if (prevFlowsRef.current !== flowsStr) {
        prevFlowsRef.current = flowsStr;
        stepHelpers.setStepData(1, { flows });
      }
    }
  }, [flows, stepHelpers]);

  // Handle flow item updates
  const handleUpdateFlow = useCallback(
    (endpointId: string, updatedData: Partial<SyncFlowItem>) => {
      if (updatedData.isActive !== undefined) {
        updateFlowMutation.mutate({
          flowId: endpointId,
          payload: { is_active: updatedData.isActive },
        });
      }
    },
    [updateFlowMutation],
  );

  // Handle flow item deletion
  const handleDeleteFlow = useCallback(
    (endpointId: string) => {
      deleteFlowMutation.mutate(endpointId);
      if (expandedEndpointId === endpointId) {
        setExpandedEndpointId(null);
      }
    },
    [expandedEndpointId, deleteFlowMutation],
  );

  // Handle adding new sync flow item (opens dialogue modal)
  const handleAddSyncFlow = () => {
    setIsDialogOpen(true);
  };

  // Callback when a flow is added from dialogue modal (handled by React Query invalidation)
  const handleAddFlowSubmit = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleContinue = () => {
    if (clearValidationError) {
      clearValidationError();
    }

    if (onNext) {
      onNext();
    }
    const targetAction = action || "create";
    navigate(`/erp-integration/neptune/${targetAction}/${id}#review`);
  };

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div>
          <PageHeader
            title="Sync Flows"
            subtitle="Each flow defines what data moves between SMART360 and Neptune, in which direction, and how often. Add as many as you need."
          />
        </div>
        <Button
          type="button"
          onClick={handleAddSyncFlow}
          className="h-10 px-4 rounded-xl font-bold bg-[#5850ec] hover:bg-[#4f46e5] text-white gap-2 flex items-center shadow-sm shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>Add Sync Flow</span>
        </Button>
      </div>

      {/* Alert Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50/60 border border-blue-100 rounded-2xl">
        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <h4 className="text-sm font-bold text-blue-900 leading-normal">
            Multi-API model
          </h4>
          <p className="text-xs text-blue-700 font-semibold leading-relaxed">
            Neptune exposes multiple endpoints — Consumers, Bills, Payments and
            Meter Reads. Each can sync independently with its own direction &
            schedule.
          </p>
        </div>
      </div>

      {/* Sync Flows List Container */}
      <div className="space-y-4">
        {flows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
            <div className="w-10 h-10 rounded-xl bg-[#eeeaff] flex items-center justify-center mb-3">
              <Plus className="w-5 h-5 text-[#5850ec]" />
            </div>
            <h4 className="text-sm font-bold text-gray-800 mb-1">
              No sync flows configured yet
            </h4>
            <p className="text-xs text-gray-500 max-w-xs mb-4">
              Add your first sync flow to define how data moves between SMART360
              and Neptune.
            </p>
            <Button
              type="button"
              onClick={handleAddSyncFlow}
              className="h-8 px-4 rounded-lg font-bold bg-[#5850ec] hover:bg-[#4f46e5] text-white text-xs gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add Sync Flow</span>
            </Button>
          </div>
        ) : (
          flows.map((flow) => (
            <SyncFlowCard
              key={flow.endpointId}
              item={flow}
              isExpanded={expandedEndpointId === flow.endpointId}
              onToggleExpand={() =>
                setExpandedEndpointId((prev) =>
                  prev === flow.endpointId ? null : flow.endpointId,
                )
              }
              onUpdate={handleUpdateFlow}
              onDelete={handleDeleteFlow}
            />
          ))
        )}
      </div>

      {/* Navigation Footer */}
      <div className="flex items-center justify-between pt-6 border-t mt-8">
        <Button onClick={onPrevious} variant="outline">
          <ChevronLeft className="w-4 h-4" />
          <span>Back</span>
        </Button>
        <div className="flex items-center gap-4">
          <Button onClick={handleContinue} variant="default">
            <span>Continue</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Add Sync Flow Dialog */}
      <CreateSyncFlowDialogue
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onAdd={handleAddFlowSubmit}
      />
    </div>
  );
};

export default SyncFlowsStep;
